import { useMemo } from "react";

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface PasswordCheck {
  label: string;
  met: boolean;
}

function getPasswordChecks(password: string): PasswordCheck[] {
  return [
    { label: "At least 8 characters", met: password.length >= 8 },
    { label: "Uppercase letter", met: /[A-Z]/.test(password) },
    { label: "Lowercase letter", met: /[a-z]/.test(password) },
    { label: "Number", met: /[0-9]/.test(password) },
  ];
}

function getStrengthLevel(checks: PasswordCheck[]): {
  label: string;
  color: string;
  width: string;
} {
  const metCount = checks.filter((c) => c.met).length;

  if (metCount === 0) return { label: "", color: "", width: "w-0" };
  if (metCount === 1)
    return {
      label: "Weak",
      color: "bg-[color:var(--color-danger)]",
      width: "w-1/4",
    };
  if (metCount === 2)
    return {
      label: "Fair",
      color: "bg-[color:var(--color-warning)]",
      width: "w-2/4",
    };
  if (metCount === 3)
    return {
      label: "Good",
      color: "bg-[color:var(--color-warning)]/70",
      width: "w-3/4",
    };
  return {
    label: "Strong",
    color: "bg-[color:var(--color-success)]",
    width: "w-full",
  };
}

export function PasswordStrengthIndicator({
  password,
}: PasswordStrengthIndicatorProps) {
  const checks = useMemo(() => getPasswordChecks(password), [password]);
  const strength = useMemo(() => getStrengthLevel(checks), [checks]);

  if (!password) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="h-1.5 flex-1 rounded-full bg-muted">
          <div
            className={`h-full rounded-full transition-all duration-300 ${strength.color} ${strength.width}`}
          />
        </div>
        {strength.label && (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {strength.label}
          </span>
        )}
      </div>
      <ul className="grid grid-cols-2 gap-1">
        {checks.map((check) => (
          <li
            key={check.label}
            className={`text-xs ${check.met ? "text-[color:var(--color-success)]" : "text-muted-foreground"}`}
          >
            {check.met ? "\u2713" : "\u2022"} {check.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
